// Scraped titles/models often repeat the brand inline (e.g. "Кофемашина рожковая
// Nuova Simonelli Oscar II" with brand "Nuova Simonelli") since the brand is
// already shown separately elsewhere in the UI — strip it out for display.
export function stripBrand(text: string, brand?: string): string {
  if (!brand) return text.trim();
  const pattern = new RegExp(brand.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
  return text.replace(pattern, "").replace(/\s{2,}/g, " ").trim();
}

// Some product names carry a trailing parenthetical aside (e.g. "Набор форм
// для жарки и выпечки, малый (4 малые формы + 1 малый противень)") that's
// useful detail but, kept inline, pushes the title past a readable 2 lines —
// split it out so callers can render it as smaller secondary text instead.
export function splitParenthetical(text: string): { main: string; detail: string | null } {
  const match = text.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  if (!match) return { main: text.trim(), detail: null };
  return { main: match[1].trim(), detail: match[2].trim() };
}

// electroluxSeed.ts's "model" field was imported from the manufacturer's own
// description column, not a real model code — e.g. "SKYLINE PROS OVEN 10 GN
// 1/1 - ELECTRIC - 3-GLASS - IOT MODULE" or "EL.BOILING PAN+AUTO.FILLING-40L
// -EASYLINE". Audited every one of this brand's ~1,130 products: this is
// true of all of them, short or long, not a scattered minority — so it's
// never rendered as "the model" (it duplicates/expands the name rather than
// identifying one specific SKU, exactly the case the check below exists
// for). Every other brand's `model` field is a genuine short code, so this
// is scoped to Electrolux Professional specifically rather than a length
// heuristic — one was tried and it also caught legitimate compact codes
// from Gram, Hoshizaki, Irinox, CB, and Firex (e.g. Gram's "SUPERIOR EURO F
// 62 CCG C1 4S" is a real model designation despite being just as long).
const BRANDS_WITH_DESCRIPTION_AS_MODEL = new Set(["Electrolux Professional"]);

// Some scraped listings have no distinct model code, so `model` duplicates
// `name` verbatim once the brand is stripped — callers should skip rendering
// the model line entirely rather than repeat the title beneath itself.
export function getProductDisplayText(name: string, model: string, brand?: string) {
  const strippedName = stripBrand(name, brand);
  const cleanedModel = stripBrand(model, brand);
  const showModel =
    cleanedModel.length > 0 &&
    cleanedModel.toLowerCase() !== strippedName.toLowerCase() &&
    !(brand && BRANDS_WITH_DESCRIPTION_AS_MODEL.has(brand));
  const { main: cleanedName, detail: nameDetail } = splitParenthetical(strippedName);
  return { cleanedName, nameDetail, cleanedModel, showModel };
}
